import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";
import { Moon, Sun, Tag } from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import { formatError } from "helpers/formatError";
import useAuthorized from "hooks/useAuthorized";
import useShowDisabledTags from "hooks/useShowDisabledTags";
import useTheme from "hooks/useTheme";
import UserService from "services/User";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "components/ui/alert-dialog";
import { Button, buttonVariants } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Label } from "components/ui/label";
import { Separator } from "components/ui/separator";
import { Switch } from "components/ui/switch";
import { toast } from "sonner";
import { queryClient } from "@/App";

export function UserPreferences() {
  const { isDark, toggleTheme } = useTheme();
  const canEdit = useAuthorized([ACL_RELATIONSHIPS.userSettings.edit]);
  const canReadSmtp = useAuthorized([ACL_RELATIONSHIPS.emailConfigSmtp.read]);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const showDisabledTags = useShowDisabledTags();
  const showDisabledTagsSetting = useMutation({
    mutationFn: UserService.updateShowDisabledTags,
    onError: error =>
      toast.error(formatError(error, "Could not save the tag setting")),
  });

  // The dialog is controlled so a failed reset keeps it open with the error
  // visible, rather than dismissing as if the reset had gone through.
  const resetTableSettings = async () => {
    setResetting(true);
    try {
      await UserService.resetPreferences();
      // Dropped rather than invalidated so a table mounted after the reset
      // can't be handed the pre-reset settings from cache while its refetch is
      // in flight.
      queryClient.removeQueries({ queryKey: ["table-settings"] });
      setResetDialogOpen(false);
      toast.success("Table settings were reset");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Preferences</CardTitle>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:items-center tw:justify-between">
          <Label
            htmlFor="theme-toggle"
            className="tw:flex tw:items-center tw:gap-2"
          >
            {isDark ? (
              <Moon className="tw:size-4" />
            ) : (
              <Sun className="tw:size-4" />
            )}
            Dark Mode
          </Label>
          <Switch
            id="theme-toggle"
            checked={isDark}
            onCheckedChange={toggleTheme}
          />
        </div>

        <div className="tw:flex tw:items-center tw:justify-between">
          <Label
            htmlFor="show-disabled-tags-toggle"
            className="tw:flex tw:items-center tw:gap-2"
          >
            <Tag className="tw:size-4" />
            Show disabled tags
          </Label>
          <Switch
            id="show-disabled-tags-toggle"
            checked={showDisabledTags}
            disabled={showDisabledTagsSetting.isPending}
            onCheckedChange={checked => showDisabledTagsSetting.mutate(checked)}
          />
        </div>

        {(canReadSmtp || canEdit) && (
          <>
            <Separator />
            <div className="tw:flex tw:flex-wrap tw:gap-2">
              {canReadSmtp && (
                <NavLink
                  to="/smtp_accounts"
                  className={buttonVariants({
                    variant: "outline",
                    size: "sm",
                  })}
                >
                  SMTP Configuration
                </NavLink>
              )}
              {canEdit && (
                <AlertDialog
                  open={resetDialogOpen}
                  onOpenChange={setResetDialogOpen}
                >
                  <AlertDialogTrigger
                    render={
                      <Button variant="destructive" size="sm">
                        Reset Table Settings
                      </Button>
                    }
                  />
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reset table settings?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will set all table settings back to their original
                        state.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={resetting}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        variant="destructive"
                        onClick={resetTableSettings}
                        disabled={resetting}
                      >
                        {resetting ? "Resetting…" : "Reset"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
